import axios, { type InternalAxiosRequestConfig } from 'axios'
import type { AxiosError } from 'axios'
import type {
  AuditDraft,
  AuditEvent,
  ConsoleDashboard,
  IncidentRecord,
  MaintenanceRecord,
  OverviewResponse,
  Paginated,
  ProjectRecord,
  ResearchOutput,
  ServiceRecord,
  StewardshipResponse,
  User,
  WorkloadRecord,
} from './types'

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 20_000,
  headers: { accept: 'application/json' },
})

let csrfToken: string | null = null
let csrfPromise: Promise<string> | null = null

export async function loadCsrfToken(force = false): Promise<string> {
  if (!force && csrfToken) return csrfToken
  if (!force && csrfPromise) return csrfPromise
  csrfPromise = api
    .get<{ csrfToken: string }>('/auth/csrf')
    .then(({ data }) => {
      csrfToken = data.csrfToken
      return data.csrfToken
    })
    .finally(() => {
      csrfPromise = null
    })
  return csrfPromise
}

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const method = config.method?.toUpperCase() ?? 'GET'
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    config.headers.set('x-csrf-token', await loadCsrfToken())
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _csrfRetried?: boolean }) | undefined
    const code = (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code
    if (config && code === 'CSRF_REJECTED' && !config._csrfRetried) {
      config._csrfRetried = true
      config.headers.set('x-csrf-token', await loadCsrfToken(true))
      return api.request(config)
    }
    throw error
  },
)

export function apiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined
    return body?.error?.message ?? error.message
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred'
}

export const publicApi = {
  overview: async (range: '24h' | '7d' | '30d' | '90d' = '30d') =>
    (await api.get<OverviewResponse>('/public/overview', { params: { range } })).data,
  ledger: async (params: Record<string, string | number | undefined>) =>
    (await api.get<Paginated<AuditEvent>>('/public/ledger', { params })).data,
  ledgerEvent: async (eventId: string) =>
    (await api.get<{ event: AuditEvent; corrections: AuditEvent[] }>(`/public/ledger/${eventId}`)).data,
  services: async () => (await api.get<{ items: ServiceRecord[] }>('/public/services')).data.items,
  research: async (params?: Record<string, string | undefined>) =>
    (await api.get<{ items: ResearchOutput[] }>('/public/research', { params })).data.items,
  stewardship: async () => (await api.get<StewardshipResponse>('/public/stewardship')).data,
}

export const authApi = {
  me: async () => (await api.get<{ user: User | null }>('/auth/me')).data.user,
  login: async (username: string, password: string) => {
    const { data } = await api.post<{ user: User; csrfToken: string }>('/auth/login', {
      username,
      password,
    })
    csrfToken = data.csrfToken
    return data
  },
  logout: async () => {
    await api.post('/auth/logout')
    csrfToken = null
  },
}

export const consoleApi = {
  dashboard: async () => (await api.get<ConsoleDashboard>('/console/dashboard')).data,
  services: async () => (await api.get<{ items: ServiceRecord[] }>('/console/services')).data.items,
  drafts: async (status?: string) =>
    (await api.get<{ items: AuditDraft[] }>('/console/drafts', { params: { status } })).data.items,
  createDraft: async (input: Record<string, unknown>) =>
    (await api.post<{ item: AuditDraft }>('/console/drafts', input)).data.item,
  patchDraft: async (id: string, input: Record<string, unknown>) =>
    (await api.patch<{ item: AuditDraft }>(`/console/drafts/${id}`, input)).data.item,
  submitDraft: async (id: string) =>
    (await api.post<{ item: AuditDraft }>(`/console/drafts/${id}/submit`)).data.item,
  approveDraft: async (id: string) =>
    (await api.post<{ event: AuditEvent }>(`/console/drafts/${id}/approve`)).data.event,
  rejectDraft: async (id: string, reason: string) =>
    (await api.post<{ item: AuditDraft }>(`/console/drafts/${id}/reject`, { reason })).data.item,
  workloads: async () => (await api.get<{ items: WorkloadRecord[] }>('/console/workloads')).data.items,
  createWorkload: async (input: Record<string, unknown>) =>
    (await api.post<{ item: WorkloadRecord }>('/console/workloads', input)).data.item,
  updateWorkloadStatus: async (id: string, status: WorkloadRecord['status']) =>
    (await api.patch<{ item: WorkloadRecord }>(`/console/workloads/${id}/status`, { status })).data.item,
  incidents: async () => (await api.get<{ items: IncidentRecord[] }>('/console/incidents')).data.items,
  createIncident: async (input: Record<string, unknown>) =>
    (await api.post<{ item: IncidentRecord }>('/console/incidents', input)).data.item,
  updateIncident: async (id: string, input: Record<string, unknown>) =>
    (await api.put<{ item: IncidentRecord }>(`/console/incidents/${id}`, input)).data.item,
  maintenance: async () =>
    (await api.get<{ items: MaintenanceRecord[] }>('/console/maintenance')).data.items,
  createMaintenance: async (input: Record<string, unknown>) =>
    (await api.post<{ item: MaintenanceRecord }>('/console/maintenance', input)).data.item,
  updateMaintenance: async (id: string, input: Record<string, unknown>) =>
    (await api.put<{ item: MaintenanceRecord }>(`/console/maintenance/${id}`, input)).data.item,
  projects: async () =>
    (await api.get<{ items: ProjectRecord[] }>('/console/catalog/projects')).data.items,
  createProject: async (input: Record<string, unknown>) =>
    (await api.post<{ item: ProjectRecord }>('/console/catalog/projects', input)).data.item,
  updateProject: async (id: string, input: Record<string, unknown>) =>
    (await api.put<{ item: ProjectRecord }>(`/console/catalog/projects/${id}`, input)).data.item,
  adminServices: async () =>
    (await api.get<{ items: ServiceRecord[] }>('/console/catalog/services')).data.items,
  createService: async (input: Record<string, unknown>) =>
    (await api.post<{ item: ServiceRecord }>('/console/catalog/services', input)).data.item,
  updateService: async (id: string, input: Record<string, unknown>) =>
    (await api.put<{ item: ServiceRecord }>(`/console/catalog/services/${id}`, input)).data.item,
  adminResearch: async () =>
    (await api.get<{ items: ResearchOutput[] }>('/console/catalog/research')).data.items,
  createResearch: async (input: Record<string, unknown>) =>
    (await api.post<{ item: ResearchOutput }>('/console/catalog/research', input)).data.item,
  updateResearch: async (id: string, input: Record<string, unknown>) =>
    (await api.put<{ item: ResearchOutput }>(`/console/catalog/research/${id}`, input)).data.item,
}
